import type { RevenueModule } from '@horevia/contracts';
import {
  BarTimeSeries,
  Card,
  CardBody,
  CardHeader,
  ChartFigure,
  Container,
  DonutChart,
  EmptyState,
  ErrorState,
  HorizontalBars,
  NoPermissionState,
  PageHeader,
  Skeleton,
  type ChartPoint,
  type ChartSeries,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { BarChart3, ChartPie } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@/features/analytics/MetricCard';
import { daysInRange } from '@/features/analytics/period';
import { useCan, useHasModule, useSession } from '@/features/auth/session';
import { ClienteleCard } from '@/features/crm/ClienteleCard';
import { InsightsToolbar } from '@/features/insights/InsightsToolbar';
import { moduleColor, moduleLabel, percentLabel } from '@/features/insights/labels';
import { analyticsOverviewQueryOptions } from '@/features/insights/queries';
import { validateInsightsSearch, type InsightsSearch } from '@/features/insights/search';
import { useInsightsPeriod } from '@/features/insights/useInsightsPeriod';
import { outletsQueryOptions } from '@/features/pos/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort, formatNumber } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

export const Route = createFileRoute('/_app/analytics/')({
  validateSearch: validateInsightsSearch,
  component: OverviewPage,
});

function axisLabel(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
    weekday: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00.000Z`));
}

/** Vue consolidée (ADR-024, D-H56) : CA par famille, chaque vente comptée une fois. */
function OverviewPage() {
  const { t, i18n } = useTranslation(['insights', 'analytics', 'common', 'errors']);
  const locale = i18n.language;
  const session = useSession();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canRead = useCan('report.operational');
  const canReadFinance = useCan('report.financial');
  const hasPos = useHasModule('pos');
  // Horevia CRM (ADR-025) : carte « Clientèle » avec le module et `crm.read`.
  const hasCrm = useHasModule('crm');
  const canReadCrm = useCan('crm.read');
  const showClientele = hasCrm && canReadCrm;
  const propertyId = useScopedPropertyId();
  const { preset, custom, range, compare } = useInsightsPeriod(search);

  const setSearch = (next: Partial<InsightsSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });

  const outlets = useQuery(outletsQueryOptions(propertyId, canRead && hasPos));
  const query = useQuery(
    analyticsOverviewQueryOptions(
      { ...range, compare, propertyId, outletId: search.outletId },
      canRead,
    ),
  );
  const data = query.data;
  const currency = data?.currencyCode ?? session.user.tenant.currencyCode;
  const modules = useMemo(() => data?.revenueByModule.map((row) => row.module) ?? [], [data]);

  const series = useMemo<ChartSeries[]>(
    () =>
      modules.map((module) => ({
        key: module,
        label: moduleLabel(t, module),
        color: moduleColor(module),
        format: (value: number) => formatAmount(value, locale, currency),
      })),
    [modules, t, locale, currency],
  );
  const points = useMemo<ChartPoint[]>(
    () =>
      (data?.daily ?? []).map((day) => ({
        key: day.date,
        label: axisLabel(day.date, locale),
        fullLabel: formatDateShort(`${day.date}T00:00:00.000Z`, locale),
        values: modules.map((module) => day.revenue[module] ?? 0),
      })),
    [data, modules, locale],
  );

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('insights:overview.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const days = daysInRange(range);
  const money = (value: number) => formatAmount(value, locale, currency);
  const empty = data ? data.revenue.value === 0 && data.revenueByModule.length === 0 : false;

  return (
    <Container>
      <PageHeader
        title={t('insights:overview.title')}
        subtitle={t('insights:overview.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('insights:title') },
          { label: t('insights:overview.title') },
        ]}
      />

      <div className="stack">
        <Card>
          <CardBody>
            <InsightsToolbar
              preset={preset}
              onPresetChange={(value) => setSearch({ period: value })}
              range={range}
              custom={custom}
              onCustomChange={(value) =>
                setSearch({ period: 'CUSTOM', from: value.from, to: value.to })
              }
              compare={compare}
              onCompareChange={(value) => setSearch({ compare: value || undefined })}
              outlets={outlets.data ?? []}
              outletId={search.outletId}
              onOutletChange={(value) => setSearch({ outletId: value })}
            />
          </CardBody>
        </Card>

        {showClientele ? <ClienteleCard range={range} propertyId={propertyId} /> : null}

        {query.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : (
          <>
            <div className="grid grid-4">
              <MetricCard
                label={t('insights:kpi.revenue')}
                display={money(data?.revenue.value ?? 0)}
                metric={data?.revenue}
                loading={query.isPending}
                showDelta={compare}
                footer={t('insights:kpi.revenueFoot', { count: days })}
              />
              {(data?.revenueByModule ?? []).map((row) => (
                <MetricCard
                  key={row.module}
                  label={moduleLabel(t, row.module)}
                  display={money(row.revenue.value)}
                  metric={row.revenue}
                  showDelta={compare}
                  footer={percentLabel(row.share, locale)}
                />
              ))}
            </div>

            {query.isPending ? (
              <Skeleton height={260} />
            ) : empty ? (
              <EmptyState
                icon={<BarChart3 className="ico ico-24" aria-hidden="true" />}
                title={t('insights:empty.title')}
                description={t('insights:empty.body')}
              />
            ) : (
              <div className="grid grid--two-thirds">
                <Card>
                  <CardHeader
                    title={t('insights:sections.daily')}
                    icon={<BarChart3 className="ico ico-16" aria-hidden="true" />}
                  />
                  <CardBody>
                    <ChartFigure
                      unit={t('insights:charts.thousands', { currency })}
                      legend={series.map((entry) => ({ label: entry.label, color: entry.color }))}
                      description={t('insights:charts.dailyDescription', {
                        from: formatDateShort(`${range.from}T00:00:00.000Z`, locale),
                        to: formatDateShort(`${range.to}T00:00:00.000Z`, locale),
                        total: money(data?.revenue.value ?? 0),
                      })}
                      table={{
                        caption: t('insights:sections.daily'),
                        toggleLabel: t('insights:charts.showData'),
                        columns: [
                          { key: 'date', label: t('insights:charts.columns.date') },
                          ...modules.map((module) => ({
                            key: module,
                            label: moduleLabel(t, module),
                            numeric: true,
                            format: (row: Record<string, unknown>) =>
                              money(Number(row[module] ?? 0)),
                          })),
                        ],
                        rows: (data?.daily ?? []).map((day) => ({
                          key: day.date,
                          date: formatDateShort(`${day.date}T00:00:00.000Z`, locale),
                          ...Object.fromEntries(
                            modules.map((module) => [module, day.revenue[module] ?? 0]),
                          ),
                        })),
                      }}
                    >
                      <BarTimeSeries
                        points={points}
                        series={series}
                        formatTick={(value) => formatNumber(Math.round(value / 1000), locale)}
                      />
                    </ChartFigure>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader
                    title={t('insights:sections.byModule')}
                    icon={<ChartPie className="ico ico-16" aria-hidden="true" />}
                  />
                  <CardBody>
                    <ChartFigure
                      description={t('insights:charts.byModuleDescription', {
                        count: data?.revenueByModule.length ?? 0,
                      })}
                      table={{
                        caption: t('insights:sections.byModule'),
                        toggleLabel: t('insights:charts.showData'),
                        columns: [
                          { key: 'module', label: t('insights:charts.columns.module') },
                          {
                            key: 'amount',
                            label: t('insights:charts.columns.amount'),
                            numeric: true,
                            format: (row) => money(Number(row['amount'])),
                          },
                          {
                            key: 'share',
                            label: t('insights:table.share'),
                            numeric: true,
                            format: (row) => percentLabel(Number(row['share']), locale),
                          },
                        ],
                        rows: (data?.revenueByModule ?? []).map((row) => ({
                          key: row.module,
                          module: moduleLabel(t, row.module),
                          amount: row.revenue.value,
                          share: row.share,
                        })),
                      }}
                    >
                      <DonutChart
                        centerValue={formatNumber(
                          Math.round((data?.revenue.value ?? 0) / 1000),
                          locale,
                        )}
                        centerLabel={t('insights:charts.thousandsShort', { currency })}
                        slices={(data?.revenueByModule ?? []).map((row) => ({
                          key: row.module,
                          label: moduleLabel(t, row.module),
                          value: row.revenue.value,
                          display: money(row.revenue.value),
                        }))}
                      />
                    </ChartFigure>
                  </CardBody>
                </Card>
              </div>
            )}

            {data?.hotel || data?.pos ? (
              <div className="grid grid-2">
                {data.hotel ? (
                  <Card>
                    <CardHeader title={t('insights:sections.hotel')} />
                    <CardBody>
                      <div className="grid grid-2">
                        <MetricCard
                          label={t('insights:kpi.occupancy')}
                          display={percentLabel(data.hotel.occupancyRate.value, locale)}
                          metric={data.hotel.occupancyRate}
                          deltaInPoints
                          showDelta={compare}
                        />
                        <MetricCard
                          label={t('insights:kpi.adr')}
                          display={money(data.hotel.averageDailyRate.value)}
                          metric={data.hotel.averageDailyRate}
                          showDelta={compare}
                        />
                        <MetricCard
                          label={t('insights:kpi.revpar')}
                          display={money(data.hotel.revPar.value)}
                          metric={data.hotel.revPar}
                          showDelta={compare}
                        />
                        <MetricCard
                          label={t('insights:kpi.stays')}
                          display={formatNumber(data.hotel.staysCount.value, locale)}
                          metric={data.hotel.staysCount}
                          showDelta={compare}
                        />
                      </div>
                    </CardBody>
                  </Card>
                ) : null}
                {data.pos ? (
                  <Card>
                    <CardHeader
                      title={t('insights:sections.pos')}
                      actions={
                        <Link className="link-quiet t-13" to="/analytics/sales" search={search}>
                          {t('insights:actions.openSales')}
                        </Link>
                      }
                    />
                    <CardBody>
                      <div className="grid grid-2">
                        <MetricCard
                          label={t('insights:kpi.orders')}
                          display={formatNumber(data.pos.ordersCount.value, locale)}
                          metric={data.pos.ordersCount}
                          showDelta={compare}
                          footer={t('insights:kpi.ordersFoot')}
                        />
                        <MetricCard
                          label={t('insights:kpi.covers')}
                          display={formatNumber(data.pos.coversCount.value, locale)}
                          metric={data.pos.coversCount}
                          showDelta={compare}
                        />
                        <MetricCard
                          label={t('insights:kpi.averageTicket')}
                          display={money(data.pos.averageTicket.value)}
                          metric={data.pos.averageTicket}
                          showDelta={compare}
                        />
                        <MetricCard
                          label={t('insights:kpi.happyHour')}
                          display={money(data.pos.happyHourTotal.value)}
                          metric={data.pos.happyHourTotal}
                          higherIsBetter={false}
                          showDelta={compare}
                        />
                        <MetricCard
                          label={t('insights:kpi.transferred')}
                          display={money(data.pos.transferredTotal.value)}
                          metric={data.pos.transferredTotal}
                          showDelta={compare}
                          footer={t('insights:kpi.transferredFoot', {
                            count: data.pos.transferredCount.value,
                          })}
                        />
                        <MetricCard
                          label={t('insights:kpi.cancelled')}
                          display={formatNumber(data.pos.cancelledCount.value, locale)}
                          metric={data.pos.cancelledCount}
                          higherIsBetter={false}
                          showDelta={compare}
                        />
                      </div>
                    </CardBody>
                  </Card>
                ) : null}
              </div>
            ) : null}

            {data && !query.isPending ? (
              <div className="grid grid-3">
                <Card>
                  <CardHeader title={t('insights:sections.finance')} />
                  <CardBody>
                    {!canReadFinance || !data.finance ? (
                      <p className="muted t-13">{t('insights:financeHidden')}</p>
                    ) : (
                      <div className="stack stack--sm">
                        <MetricCard
                          label={t('insights:kpi.collected')}
                          display={money(data.finance.collected.value)}
                          metric={data.finance.collected}
                          showDelta={compare}
                          footer={t('insights:kpi.collectedFoot')}
                        />
                        <MetricCard
                          label={t('insights:kpi.expenses')}
                          display={money(data.finance.expenses.value)}
                          metric={data.finance.expenses}
                          higherIsBetter={false}
                          showDelta={compare}
                        />
                        {data.finance.costOfSales ? (
                          <MetricCard
                            label={t('insights:kpi.costOfSales')}
                            display={money(data.finance.costOfSales.value)}
                            metric={data.finance.costOfSales}
                            higherIsBetter={false}
                            showDelta={compare}
                          />
                        ) : null}
                        {data.finance.grossMargin ? (
                          <MetricCard
                            label={t('insights:kpi.grossMargin')}
                            display={money(data.finance.grossMargin.value)}
                            metric={data.finance.grossMargin}
                            showDelta={compare}
                            footer={
                              data.finance.grossMarginRate === null
                                ? undefined
                                : `${t('insights:kpi.grossMarginRate')} ${percentLabel(data.finance.grossMarginRate, locale)}`
                            }
                          />
                        ) : null}
                        {data.finance.collectedByMethod.length > 0 ? (
                          <HorizontalBars
                            items={data.finance.collectedByMethod.map((row) => ({
                              key: `${row.method}|${row.provider ?? ''}`,
                              label: [
                                t(`insights:methods.${row.method}`, { defaultValue: row.method }),
                                row.provider,
                              ]
                                .filter(Boolean)
                                .join(' · '),
                              value: row.amount,
                              display: money(row.amount),
                              sub:
                                t('insights:charts.columns.count') +
                                ` : ${formatNumber(row.count, locale)}`,
                            }))}
                          />
                        ) : null}
                      </div>
                    )}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader title={t('insights:sections.topOutlets')} />
                  <CardBody>
                    {data.topOutlets.length === 0 ? (
                      <p className="muted t-13">{t('insights:empty.title')}</p>
                    ) : (
                      <HorizontalBars
                        items={data.topOutlets.map((row) => ({
                          key: row.outletId,
                          label: row.name,
                          value: row.revenue,
                          display: money(row.revenue),
                          sub: `${formatNumber(row.ordersCount, locale)} ${t('insights:table.orders').toLowerCase()}`,
                          color: moduleColor(row.kind as RevenueModule),
                        }))}
                      />
                    )}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader title={t('insights:sections.topProducts')} />
                  <CardBody>
                    {data.topProducts.length === 0 ? (
                      <p className="muted t-13">{t('insights:empty.title')}</p>
                    ) : (
                      <HorizontalBars
                        items={data.topProducts.map((row) => ({
                          key: row.productId ?? row.label,
                          label: row.label,
                          value: row.amount,
                          display: money(row.amount),
                          sub: `${formatNumber(row.quantity, locale)} × `,
                        }))}
                      />
                    )}
                  </CardBody>
                </Card>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Container>
  );
}
